import { useCompanionOnboardingStore } from '@/stores/companion-onboarding-store';
import { useBrandDnaQuery } from '@/hooks/queries/use-brand-dna-query';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useDiscoveryPreferencesQuery } from '@/hooks/queries/use-discovery-preferences-query';
import { useMemo } from 'react';

type ResumeStep = 'dna' | 'creators' | 'ready';

type CompanionOnboardingStatus = {
  isEnabled: boolean;
  /**
   * True while the DNA query is still resolving. hasDna is false during
   * resolution even for brands that do have DNA, so consumers gating on it
   * (e.g. the wizard step clamp) must treat the status as provisional until
   * this flips to false.
   */
  isResolving: boolean;
  hasDna: boolean;
  hasStarted: boolean;
  isCompleted: boolean;
  isBannerDismissed: boolean;
  shouldShowResumeCard: boolean;
  /**
   * True for existing clients who already have DNA but have not yet started
   * the wizard. The banner is an invitation to begin. Once the brand starts
   * the wizard (hasStarted), the sidebar resume card takes over and this
   * banner is no longer shown.
   */
  shouldShowBanner: boolean;
  resumeStep: ResumeStep;
  stepsDone: 0 | 1 | 2;
};

/**
 * Single write gateway for the companion onboarding store.
 *
 * This is the ONLY hook allowed to read OR write useCompanionOnboardingStore.
 * All components that need to record progress must use this hook rather than
 * importing the store directly.
 */
export const useCompanionOnboardingActions = () => {
  const dismissBanner = useCompanionOnboardingStore((state) => state.dismissBanner);
  return { dismissBanner };
};

/**
 * Single source of truth for companion onboarding UI state.
 *
 * Combines the agents feature flag, brand DNA availability, and the
 * per-brand completion store. This is the ONLY file allowed to read
 * OR write useCompanionOnboardingStore — all other components consume
 * this hook or useCompanionOnboardingActions.
 *
 * isCompleted is derived from brand.companionOnboardedAt (server-authoritative).
 * hasStarted, prefsConfirmedAt, bannerDismissedAt remain local (Zustand).
 *
 * hasDna stays false until the DNA query resolves. isPending (not isLoading)
 * is required here: the DNA query is disabled until the current brand resolves,
 * and a disabled query has isLoading=false but isPending=true, so isPending
 * covers both "brand not resolved yet" and "dna fetching".
 */
export const useCompanionOnboardingStatus = (
  brandId: string | undefined
): CompanionOnboardingStatus => {
  // Companion onboarding is GA for every brand; agentic surfaces no longer
  // consult a feature flag. `isEnabled` stays in the status shape so consumers
  // keep a single visibility source, but it is now always on.
  const isEnabled = true;

  const { data: dnaData, isPending: isDnaPending } = useBrandDnaQuery();
  const hasDna = !isDnaPending && !!dnaData?.brandDna;

  const { data: brand } = useCurrentBrandQuery();

  // prefsConfirmed is server-derived: confirming prefs is the only path that
  // persists brand_discovery_preferences (extraction never persists), so a
  // non-null prefs row means the brand confirmed. Brand-keyed query, disabled
  // until brandId resolves (isPending stays true while disabled).
  const { data: prefsData, isPending: isPrefsPending } = useDiscoveryPreferencesQuery(
    brandId ?? ''
  );

  const entry = useCompanionOnboardingStore((state) =>
    brandId ? (state.byBrandId[brandId] ?? null) : null
  );

  // hasStarted / isCompleted are server-authoritative: companionStartedAt is
  // stamped on wizard entry, companionOnboardedAt on completion.
  const hasStarted = !!brand?.companionStartedAt;
  const isCompleted = !!brand?.companionOnboardedAt;
  const isBannerDismissed = !!entry?.bannerDismissedAt;
  const prefsConfirmed = !!prefsData;

  return useMemo(() => {
    const shouldShowResumeCard = isEnabled && hasStarted && !isCompleted;

    // Banner targets existing clients: DNA is already set but the wizard has
    // never been started. Once the brand starts (hasStarted), the sidebar
    // resume card takes over — the banner must not compete with it.
    const shouldShowBanner =
      isEnabled && !isDnaPending && hasDna && !isCompleted && !hasStarted && !isBannerDismissed;

    // 'ready' is a real resume target, not a leaked guard value: a brand that
    // confirmed preferences but quit before finishing lands back on the final
    // step (aha) when resuming. A brand with DNA but unconfirmed prefs resumes
    // at the first preference sub-step (`creators`).
    const resumeStep: ResumeStep = !hasDna ? 'dna' : !prefsConfirmed ? 'creators' : 'ready';

    const stepsDone: 0 | 1 | 2 = hasDna ? (prefsConfirmed ? 2 : 1) : 0;

    return {
      isEnabled,
      isResolving: isDnaPending || isPrefsPending,
      hasDna,
      hasStarted,
      isCompleted,
      isBannerDismissed,
      shouldShowResumeCard,
      shouldShowBanner,
      resumeStep,
      stepsDone,
    };
  }, [
    isEnabled,
    isDnaPending,
    isPrefsPending,
    hasDna,
    hasStarted,
    isCompleted,
    isBannerDismissed,
    prefsConfirmed,
  ]);
};
